import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@shadcn-in-nx/ui/alert-dialog";
import { useBluelagoon } from "@bluelagoon";
import { Loader2, ShieldOff, AlertCircle, CheckCircle } from "lucide-react";

export default function UsageLimitBypassPanel({ companyId, companyName, currentBypass, onUpdated }) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);
    const { call, isLoading } = useBluelagoon({ method: "setUsageLimitBypass" });

    const nextValue = !currentBypass;

    const handleConfirm = async () => {
        setConfirmOpen(false);
        setSuccess(null);
        setError(null);
        try {
            const res = await call({ companyId, enabled: nextValue });
            if (res?.data) {
                setSuccess(res.data.message);
                onUpdated();
            }
        } catch (err) {
            setError(err?.error || "Failed to toggle bypass");
        }
    };

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                    <ShieldOff className="h-5 w-5" />
                    File / Row Cap Bypass
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                    {currentBypass
                        ? "This company is exempt from file & row caps. Free-tier blocks and the 14-day paid grace period do not apply."
                        : "Bypass is OFF. Standard plan caps apply (free-tier blocks immediately; paid tier enters 14-day grace when over cap)."}
                </p>
                <Button
                    onClick={() => setConfirmOpen(true)}
                    disabled={isLoading}
                    variant={currentBypass ? "destructive" : "default"}
                >
                    {isLoading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                    {currentBypass ? "Disable Bypass" : "Enable Bypass"}
                </Button>
                {error && <div className="flex items-center gap-2 text-red-600 text-sm"><AlertCircle className="h-4 w-4" />{error}</div>}
                {success && <div className="flex items-center gap-2 text-green-600 text-sm"><CheckCircle className="h-4 w-4" />{success}</div>}

                <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>
                                {nextValue ? "Enable Usage Bypass" : "Disable Usage Bypass"}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                {nextValue
                                    ? `Lift all file/row caps for ${companyName}? Any active 14-day grace period will be cleared.`
                                    : `Reapply plan caps to ${companyName}? They will be subject to free-tier blocks or paid-tier grace if currently over cap.`}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleConfirm}>Confirm</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </CardContent>
        </Card>
    );
}
